/**
 * E024-S002-T004: Excel Import Preview Component
 * E024-S001-T006: DocSelector Integration
 *
 * Shows preview of Excel files with sheet selection before import.
 */

import { Button, Checkbox, Switch, Tooltip } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { useCallback, useEffect, useRef, useState } from 'react';

import { type BasePreviewProps } from '../../../../modules/streamlain/file-import';
import {
  DatabaseImportParserWorkerUnavailableError,
  preflightExcelFileInWorker,
} from '../../../../modules/streamlain/file-import/database-import-parser-worker-client';
import type { DatabaseImportParserProgress } from '../../../../modules/streamlain/file-import/database-import-parser-worker-types';
import { DocSelector } from '../../../../modules/streamlain/sidebar/components';
import { ImportAnalysisLoading } from '../analysis-loading';
import {
  formatImportCount,
  formatImportFilesSelected,
  formatImportMoreRows,
} from '../preview-i18n';
import * as styles from './styles.css';
import type { ExcelImportConfig, ExcelPreviewData } from './types';

interface ExcelImportPreviewProps extends BasePreviewProps<ExcelImportConfig> {
  /** E024-S002-T006: Target doc ID for drag & drop (makes selector read-only) */
  targetDocId?: string;
  /** E024-S002-T006: Target doc name for display */
  targetDocName?: string;
  useDatabaseImportJob?: boolean;
}

export function ExcelImportPreview({
  files,
  onImport,
  onCancel,
  importing = false,
  targetDocId,
  targetDocName,
  useDatabaseImportJob = false,
}: ExcelImportPreviewProps) {
  const t = useI18n();
  const tRef = useRef(t);
  const [previews, setPreviews] = useState<ExcelPreviewData[]>([]);
  const [docName, setDocName] = useState(
    files.length === 1
      ? files[0].name.replace(/\.xlsx$/i, '')
      : t['com.streamlain.import.preview.excel.default-document-name']()
  );
  const [existingDocId, setExistingDocId] = useState<string | undefined>(
    targetDocId
  );
  const [hasHeader, setHasHeader] = useState(true);
  const [selectedFile, setSelectedFile] = useState(0);
  const [selectedSheet, setSelectedSheet] = useState(0);
  const [titleColumnIndex, setTitleColumnIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [analysisProgress, setAnalysisProgress] =
    useState<DatabaseImportParserProgress | null>(null);

  // E024-S002-T006: Check if this is a drag & drop import (read-only mode)
  const isReadOnly = !!targetDocId;

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  // Preflight files before any full extraction or document mutation.
  useEffect(() => {
    const controller = new AbortController();

    async function parseFiles() {
      setLoading(true);
      setLoadError(null);
      setAnalysisProgress(null);
      const results: ExcelPreviewData[] = [];

      for (const file of files) {
        try {
          const preflight = await preflightExcelFileInWorker(
            {
              file,
              hasHeader,
            },
            controller.signal,
            progress => {
              if (!controller.signal.aborted) {
                setAnalysisProgress(progress);
              }
            }
          );
          if (controller.signal.aborted) return;

          results.push({
            fileName: file.name,
            sheets: preflight.sheets.map(sheet => ({
              name: sheet.sheetName ?? sheet.source,
              selected: true,
              columns: sheet.columns.map(column => ({
                name: column.name,
                type: column.type,
              })),
              previewRows: sheet.previewRows,
              totalRows: sheet.estimate.rowCount,
              preflight: sheet,
            })),
          });
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') return;
          console.error(`[ExcelPreview] Failed to parse ${file.name}:`, error);
          const currentT = tRef.current;
          setLoadError(
            error instanceof DatabaseImportParserWorkerUnavailableError
              ? currentT[
                  'com.streamlain.import.status.failed.message.parser-unavailable'
                ]()
              : currentT['com.streamlain.import.status.failed.message']()
          );
        }
      }

      if (controller.signal.aborted) return;
      setPreviews(results);
      setTitleColumnIndex(0); // Reset title column when files change
      setLoading(false);
    }

    parseFiles().catch(error => {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error(error);
      if (!controller.signal.aborted) {
        const currentT = tRef.current;
        setLoadError(
          error instanceof DatabaseImportParserWorkerUnavailableError
            ? currentT[
                'com.streamlain.import.status.failed.message.parser-unavailable'
              ]()
            : currentT['com.streamlain.import.status.failed.message']()
        );
        setLoading(false);
      }
    });

    return () => {
      controller.abort();
    };
  }, [files, hasHeader]);

  const toggleSheetSelection = useCallback(
    (fileIndex: number, sheetIndex: number) => {
      setPreviews(prev => {
        // Create deep copy to ensure React detects the change
        const updated = prev.map((file, fIdx) => {
          if (fIdx !== fileIndex) return file;
          return {
            ...file,
            sheets: file.sheets.map((sheet, sIdx) => {
              if (sIdx !== sheetIndex) return sheet;
              return { ...sheet, selected: !sheet.selected };
            }),
          };
        });
        return updated;
      });
    },
    []
  );

  const handleImport = useCallback(() => {
    // Build selectedSheets map
    const selectedSheets = new Map<string, string[]>();
    for (const preview of previews) {
      const selected = preview.sheets.filter(s => s.selected).map(s => s.name);
      if (selected.length > 0) {
        selectedSheets.set(preview.fileName, selected);
      }
    }

    onImport({
      files,
      newDocName: docName,
      hasHeader,
      selectedSheets,
      titleColumnIndex,
      estimatedTotalRows: previews.reduce(
        (sum, preview) =>
          sum +
          preview.sheets
            .filter(sheet => sheet.selected)
            .reduce((sheetSum, sheet) => sheetSum + sheet.totalRows, 0),
        0
      ),
      existingDocId,
    });
  }, [
    files,
    docName,
    hasHeader,
    previews,
    titleColumnIndex,
    existingDocId,
    onImport,
  ]);

  // E024-S001-T006: Handle doc selection change
  const handleDocChange = useCallback(
    (value: string, isExisting: boolean, docId?: string) => {
      setDocName(value);
      setExistingDocId(isExisting ? docId : undefined);
    },
    []
  );

  const currentFile = previews[selectedFile];
  const currentSheet = currentFile?.sheets[selectedSheet];
  const selectedSheetPreflights = previews.flatMap(preview =>
    preview.sheets.filter(sheet => sheet.selected)
  );
  const tooLargeSheets = selectedSheetPreflights.filter(sheet =>
    sheet.preflight.estimate.allowedAction.startsWith('blocked-')
  );
  const canImportDirect = !useDatabaseImportJob && tooLargeSheets.length === 0;
  const canImportJob = useDatabaseImportJob && tooLargeSheets.length === 0;
  const canImport = canImportDirect || canImportJob;
  const totalSelectedSheets = previews.reduce(
    (sum, p) => sum + p.sheets.filter(s => s.selected).length,
    0
  );
  const totalRows = previews.reduce(
    (sum, p) =>
      sum +
      p.sheets
        .filter(s => s.selected)
        .reduce((s, sheet) => s + sheet.totalRows, 0),
    0
  );
  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KiB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
  };
  const totalFileBytes = files.reduce((sum, file) => sum + file.size, 0);

  if (loading) {
    return (
      <div className={styles.container}>
        <ImportAnalysisLoading
          title={t['com.streamlain.import.preview.excel.analysis.title']()}
          progress={analysisProgress}
          onCancel={onCancel}
        />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>{loadError}</div>
        <div className={styles.footer}>
          <Button variant="secondary" onClick={onCancel}>
            {t['Cancel']()}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <div className={styles.title}>
            {t['com.streamlain.import.preview.excel.title']()}
          </div>
          {currentSheet && (
            <div className={styles.preflightSummary}>
              <span className={styles.preflightMetric}>
                {formatImportCount(t, 'sheet', totalSelectedSheets)}
              </span>
              <span className={styles.preflightMetric}>
                {formatImportCount(t, 'row', totalRows)}
              </span>
              <span className={styles.preflightMetric}>
                {formatImportCount(t, 'column', currentSheet.columns.length)}
              </span>
              <span className={styles.preflightMetric}>
                {formatBytes(totalFileBytes)}
              </span>
            </div>
          )}
        </div>
        <div className={styles.subtitle}>
          {formatImportFilesSelected(t, files.length)}
        </div>
      </div>

      <div className={styles.content}>
        {/* File List (if multiple files) */}
        {files.length > 1 && (
          <div>
            <div className={styles.sectionTitle}>
              {t['com.streamlain.import.preview.files']()}
            </div>
            <div className={styles.fileList}>
              {previews.map((preview, index) => (
                <div
                  key={preview.fileName}
                  className={styles.fileItem}
                  data-selected={index === selectedFile}
                  onClick={() => {
                    setSelectedFile(index);
                    setSelectedSheet(0);
                  }}
                >
                  <span>{preview.fileName}</span>
                  <span className={styles.sheetCount}>
                    {t['com.streamlain.import.preview.sheet-selection-count']({
                      selected: String(
                        preview.sheets.filter(s => s.selected).length
                      ),
                      total: String(preview.sheets.length),
                    })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sheet List */}
        {currentFile && currentFile.sheets.length > 0 && (
          <div>
            <div className={styles.sectionTitle}>
              {t['com.streamlain.import.preview.excel.sheets-in-file']({
                fileName: currentFile.fileName,
              })}
            </div>
            <div className={styles.sheetList}>
              {currentFile.sheets.map((sheet, index) => (
                <div
                  key={sheet.name}
                  className={styles.sheetItem}
                  data-selected={index === selectedSheet}
                  onClick={() => setSelectedSheet(index)}
                >
                  <Checkbox
                    className={styles.sheetCheckbox}
                    checked={sheet.selected}
                    onChange={() => toggleSheetSelection(selectedFile, index)}
                    onClick={e => e.stopPropagation()}
                  />
                  <span className={styles.sheetName}>{sheet.name}</span>
                  <span className={styles.rowCount}>
                    {formatImportCount(t, 'row', sheet.totalRows)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Preview Table */}
        {currentSheet && (
          <div className={styles.previewTable}>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    {currentSheet.columns.map((col, i) => (
                      <Tooltip
                        key={i}
                        content={
                          i === titleColumnIndex
                            ? t[
                                'com.streamlain.import.preview.title-column.tooltip.current'
                              ]()
                            : t[
                                'com.streamlain.import.preview.title-column.tooltip.set'
                              ]()
                        }
                      >
                        <th
                          className={styles.th}
                          data-title-column={i === titleColumnIndex}
                          onClick={() => setTitleColumnIndex(i)}
                        >
                          {col.name}
                          {i === titleColumnIndex && (
                            <span className={styles.titleBadge}>
                              {t[
                                'com.streamlain.import.preview.title-column'
                              ]()}
                            </span>
                          )}
                        </th>
                      </Tooltip>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentSheet.previewRows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td
                          key={cellIndex}
                          className={styles.td}
                          data-title-column={cellIndex === titleColumnIndex}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {currentSheet.totalRows > 10 && (
              <div className={styles.moreRows}>
                {formatImportMoreRows(t, currentSheet.totalRows - 10)}
              </div>
            )}
          </div>
        )}

        {(useDatabaseImportJob || !canImportDirect) && (
          <div className={styles.blocked}>
            {tooLargeSheets.length > 0
              ? t['com.streamlain.import.preview.excel.blocked.too-large']()
              : useDatabaseImportJob
                ? existingDocId
                  ? t[
                      'com.streamlain.import.preview.excel.server-job.existing-doc'
                    ]()
                  : t[
                      'com.streamlain.import.preview.excel.server-job.new-doc'
                    ]()
                : t[
                    'com.streamlain.import.preview.excel.blocked.not-importable'
                  ]()}
          </div>
        )}

        {/* Options */}
        <div className={styles.options}>
          <div className={styles.option}>
            <label className={styles.optionLabel}>
              {t['com.streamlain.import.preview.document']()}
            </label>
            <DocSelector
              value={docName}
              onChange={handleDocChange}
              placeholder={t[
                'com.streamlain.import.preview.document.placeholder'
              ]()}
              readOnly={isReadOnly}
              readOnlyLabel={targetDocName || docName}
            />
          </div>

          <div className={styles.option}>
            <label className={styles.optionLabel}>
              {t['com.streamlain.import.preview.first-row-header']()}
            </label>
            <Switch checked={hasHeader} onChange={setHasHeader} />
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <Button variant="secondary" onClick={onCancel} disabled={importing}>
          {t['Cancel']()}
        </Button>
        <Button
          variant="primary"
          onClick={handleImport}
          disabled={importing || totalSelectedSheets === 0 || !canImport}
        >
          {importing
            ? t['com.streamlain.import.preview.action.importing']()
            : canImportJob
              ? t['com.streamlain.import.preview.action.start-jobs']({
                  count: String(totalSelectedSheets),
                })
              : t['com.streamlain.import.preview.excel.action.import-sheets']({
                  count: String(totalSelectedSheets),
                })}
        </Button>
      </div>
    </div>
  );
}
